/**
 * Admin Dashboard Formatting Utilities
 * 
 * Centralized formatting functions for dates, numbers, currency, and durations
 * with consistent locale handling.
 */
import { createLocaleFormatters } from '../localization/formatters';

type LocaleFormatters = ReturnType<typeof createLocaleFormatters>;

/**
 * Format a Date object to local YYYY-MM-DD string without timezone conversion
 * @param date - Date object to format
 * @returns YYYY-MM-DD formatted string
 */
export const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a YYYY-MM-DD string into a local Date object
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Local Date object (month is 0-indexed internally)
 */
export const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

/** Binds admin presentation formatting to the app's effective UI locale. */
export const createAdminDisplayFormatters = (formatters: LocaleFormatters) => ({
  formatNumber: (value: number | undefined | null): string => (
    value === undefined || value === null ? '-' : formatters.formatNumber(value)
  ),
  formatCurrency: (value: number | undefined | null): string => (
    formatters.formatCurrency(value ?? 0, 'USD')
  ),
  formatDuration: (milliseconds: number | undefined | null): string => {
    if (milliseconds === undefined || milliseconds === null) return '-';

    return milliseconds < 1000
      ? formatters.formatUnit(milliseconds, 'millisecond', {
        unitDisplay: 'narrow',
        maximumFractionDigits: 0,
      })
      : formatters.formatUnit(milliseconds / 1000, 'second', {
        unitDisplay: 'narrow',
        maximumFractionDigits: 2,
      });
  },
  formatLocalDateTime: (isoString?: string | null): string => {
    if (!isoString || !isoString.includes('T')) return isoString || '-';

    const dateString = isoString.endsWith('Z') || isoString.includes('+')
      ? isoString
      : `${isoString}Z`;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    return formatters.formatDateTime(date, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  },
  formatDisplayDate: (dateString: string): string => {
    if (!dateString) return '-';
    const date = parseLocalDate(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    return formatters.formatDate(date, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  },
});

/**
 * Format text to truncate and add ellipsis
 * @param text - Text to truncate
 * @param length - Maximum length before truncating
 * @returns Truncated text with ellipsis if needed
 */
export const truncateText = (text: string | undefined, length: number = 50): string => {
  if (!text) return '-';
  if (text.length <= length) return text;
  return text.substring(0, length) + '...';
};

/**
 * Get default date range for a specific dashboard context
 * @param context - Context type ('default' = 7 days, 'reports' = 6 months)
 * @returns Object with startDate and endDate
 */
export const getDefaultDateRange = (context: 'default' | 'reports' = 'default') => {
  const endDate = new Date();
  const startDate = new Date();

  if (context === 'reports') {
    startDate.setMonth(startDate.getMonth() - 6);
  } else {
    startDate.setDate(startDate.getDate() - 7);
  }

  return { startDate, endDate };
};
